import { motion } from 'motion/react'
import { Link } from 'react-router'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/shadcn/card'
import { clsx } from 'clsx'
import { Badge } from '@/components/shadcn/badge'
import { RestSchemas } from '@/contexts/QueryProvider'
import { TooltipButton } from '@/components/ui/TooltipButton'
import { ApplicationIcon } from '@/const'
import { presenceAnimation } from './ApplicationsGrid'
import { CardActionsMenu } from './CardActionsMenu'
import { cn } from '@/lib/utils'

type MessagesApplicationsApplication = RestSchemas['Messages.Applications.Application']

export const statusBadge = {
  Live: 'bg-green-50 text-green-700 border-green-700 hover:bg-green-50 hover:text-green-700 hover:border-green-700',
  Developing: 'bg-red-50 text-red-700 border-red-700 hover:bg-red-50 hover:text-red-700 hover:border-red-700',
  'Coming Soon':
    'bg-yellow-50 text-yellow-700 border-yellow-700 hover:bg-yellow-50 hover:text-yellow-700 hover:border-yellow-700',
}

export function ApplicationsGridItem({
  application,
  className,
}: {
  application: MessagesApplicationsApplication
  className?: string
}) {
  return (
    <motion.div
      className={cn('relative overflow-visible', className)}
      layout
      key={application.applicationId}
      {...presenceAnimation}
    >
      <div className="absolute right-1 top-1">
        <CardActionsMenu app={application} />
      </div>
      <Link
        className="block h-full overflow-visible rounded-xl bg-background transition-all"
        to={`/applications/${application.appKey}`}
      >
        <Card className="h-full justify-between border-none duration-500 hover:bg-primary/5 hover:shadow-md">
          <CardHeader>
            <div className="flex items-center gap-x-3">
              <TooltipButton tooltip={application.registered ? 'Registered' : 'Unregistered'}>
                <div
                  className={clsx(
                    'flex size-14 shrink-0 items-center justify-center rounded-full bg-offwhite text-foreground/50',
                    { 'bg-primary/5 text-primary': application.registered }
                  )}
                >
                  <ApplicationIcon className="size-6" />
                </div>
              </TooltipButton>
              <div className="flex w-full flex-col gap-y-1">
                <CardTitle className="text-lg font-medium leading-tight">{application.appName}</CardTitle>
                {/* <Badge
                  className={clsx(
                    'h-4 w-fit font-light',
                    statusBadge[application.status as keyof typeof statusBadge] ||
                      'border-muted bg-muted text-foreground/50'
                  )}
                  variant="secondary"
                >
                  {application.status}
                </Badge> */}
                <Badge
                  className="h-4 w-fit"
                  variant={
                    application.status === 'Live'
                      ? 'success'
                      : application.status === 'Developing'
                        ? 'warning'
                        : 'error'
                  }
                >
                  {application.status}
                </Badge>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <CardDescription className="line-clamp-2 leading-tight">{application.description}</CardDescription>
          </CardContent>
          <CardFooter>
            <div className="flex w-full justify-between gap-x-1">
              <Badge
                className="bg-muted font-medium text-foreground hover:bg-muted hover:text-foreground"
                variant="secondary"
              >
                {application.appKey}
              </Badge>
              <div className="flex items-center gap-x-1">
                <Badge variant="outline" className="font-light">
                  Roles: <span className="ml-1 font-bold">{application.roleCount}</span>
                </Badge>
                <Badge variant="outline" className="font-light">
                  Teams: <span className="ml-1 font-bold">{application.appTeamCount}</span>
                </Badge>
              </div>
            </div>
          </CardFooter>
        </Card>
      </Link>
    </motion.div>
  )
}
